// Replace a part everywhere (e.g. GL18 → FJH10K): the BOMs that use it, which of them to change, and
// from when. A family's newest draft is changed in place; one with only an active revision gets a new
// draft (the active revision never changes). Drafts keep the date for their release, unless "release
// now" is ticked. The server does it in one go: bom.replace_part (server/src/mutations/boms.ts).
import { X } from "lucide-react";
import { useMemo, useState } from "react";
import { replaceTargets, revisionState, whereUsed } from "../../../shared/boms.ts";
import { useBomIndex } from "../lib/boms.ts";
import { mutateWithResult } from "../lib/mutate.ts";
import { projectsOfItem, useProjectIndex } from "../lib/projects.ts";
import { Badge, Button, cx, Input } from "../ui/controls.tsx";
import { DialogLayer } from "../ui/DialogLayer.tsx";
import { toast } from "../ui/toast.tsx";
import { PartPicker } from "./BomPicker.tsx";

export function ReplacePartDialog({ fromId, toId, onClose }: { fromId?: string; toId?: string; onClose: () => void }) {
  const ix = useBomIndex();
  const px = useProjectIndex();
  const [from, setFrom] = useState<string | null>(fromId ?? null);
  const [to, setTo] = useState<string | null>(toId ?? null); // Part groups --- an end-of-life part's current sibling
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [date, setDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [release, setRelease] = useState(false);
  const [busy, setBusy] = useState(false);
  const fromItem = from ? ix.itemById.get(from) : undefined;
  const toItem = to ? ix.itemById.get(to) : undefined;
  // The part to put in is looked for in the replaced part's projects first (Manufacturing projects).
  const fromProjects = useMemo(() => {
    if (!fromItem) return [];
    const p = projectsOfItem(px, fromItem);
    return [...p.inUse, ...p.onDraft];
  }, [px, fromItem]);
  const targets = useMemo(() => (from ? replaceTargets(ix, from) : []), [ix, from]);
  const chosen = targets.filter((t) => !skipped.has(t.key));
  // Families that get the change through a sub-assembly being changed.
  const through = useMemo(() => {
    const names = new Set<string>();
    const walk = (us: ReturnType<typeof whereUsed>) => {
      for (const u of us) {
        for (const p of u.parents) names.add(p.label);
        walk(u.parents);
      }
    };
    if (from) walk(whereUsed(ix, from));
    return [...names];
  }, [ix, from]);

  const run = async () => {
    if (!from || !to || !chosen.length || busy) return;
    setBusy(true);
    const res = await mutateWithResult("bom.replace_part", {
      from_item_id: from,
      to_item_id: to,
      bom_ids: chosen.map((t) => t.bom.id),
      effective_from: date || null,
      release,
    });
    setBusy(false);
    if (!res.ok) return;
    const changed = (res.result as { changed: { created: boolean }[] }).changed;
    const made = changed.filter((c) => c.created).length;
    toast.ok(
      `${fromItem?.sku} → ${toItem?.sku} in ${changed.length} BOM${changed.length === 1 ? "" : "s"}` +
        `${made ? ` (${made} new draft${made === 1 ? "" : "s"})` : ""}${release ? `, released from ${date}` : ""}`,
    );
    onClose();
  };

  const chip = (item: typeof fromItem, clear: () => void) => (
    <span className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-line bg-surface-2 px-2">
      <span className="font-mono text-[12px]">{item?.sku}</span>
      <span className="min-w-0 flex-1 truncate text-muted">{item?.name}</span>
      <button type="button" onClick={clear} className="rounded p-0.5 text-faint hover:bg-hover hover:text-text" title="Pick another">
        <X className="size-3.5" />
      </button>
    </span>
  );

  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex max-h-[78vh] w-[640px] flex-col gap-3 rounded-xl border border-line bg-surface p-4 shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="text-[15px] font-semibold">Replace a part in BOMs</div>
        <div className="flex items-center gap-2">
          <span className="w-16 shrink-0 text-muted">Replace</span>
          {fromItem ? (
            chip(fromItem, () => {
              setFrom(null);
              setSkipped(new Set());
            })
          ) : (
            <PartPicker ix={ix} autoFocus onPick={(i) => setFrom(i.id)} placeholder="The part to take out… (SKU or name)" className="flex-1" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="w-16 shrink-0 text-muted">with</span>
          {toItem ? (
            chip(toItem, () => setTo(null))
          ) : (
            <PartPicker
              key={fromItem?.id ?? ""}
              projectIds={fromProjects}
              exclude={fromItem?.id}
              ix={ix}
              autoFocus={!!fromItem}
              onPick={(i) => setTo(i.id)}
              placeholder="The part to put in… (SKU or name)"
              className="flex-1"
            />
          )}
        </div>

        {fromItem && (
          <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-line">
            {targets.length === 0 && <div className="p-3 text-muted">No BOM uses {fromItem.sku || fromItem.name} (in a draft or the revision in use).</div>}
            {targets.map((t) => (
              <label key={t.key} className="flex cursor-pointer items-center gap-2 border-b border-line px-3 py-1.5 last:border-b-0 hover:bg-hover">
                <input
                  type="checkbox"
                  checked={!skipped.has(t.key)}
                  onChange={(e) => {
                    const next = new Set(skipped);
                    if (e.target.checked) next.delete(t.key);
                    else next.add(t.key);
                    setSkipped(next);
                  }}
                  className="accent-[var(--accent)]"
                />
                <span className="min-w-0 flex-1 truncate">{t.label}</span>
                {t.action === "edit_draft" ? (
                  <Badge tone="accent">change draft Rev {t.bom.revision}</Badge>
                ) : (
                  <Badge tone="ok">
                    new draft from Rev {t.bom.revision} ({revisionState(ix, t.bom)})
                  </Badge>
                )}
                <span className="w-28 shrink-0 text-right text-[12px] text-muted">
                  {t.of > 1 ? `${t.variants} of ${t.of} variants` : `${t.lines} line${t.lines === 1 ? "" : "s"}`}
                </span>
              </label>
            ))}
          </div>
        )}
        {through.length > 0 && <div className="text-[12px] text-muted">Through sub-assemblies this also reaches {through.join(", ")}.</div>}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-2">
            <span className="text-muted">Takes effect</span>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-7 w-36" />
          </label>
          <label className="flex cursor-pointer items-center gap-1.5">
            <input type="checkbox" checked={release} onChange={(e) => setRelease(e.target.checked)} className="accent-[var(--accent)]" />
            <span>Release them now</span>
          </label>
        </div>
        <div className={cx("text-[12px]", release ? "text-warn" : "text-faint")}>
          {release
            ? `Each changed draft is released from ${date || "today"}; from then on it replaces the revision in use.`
            : "Active revisions never change: they get a new draft. Drafts keep the date for when you release them."}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!fromItem || !toItem || !chosen.length || busy} onClick={run}>
            Replace in {chosen.length} BOM{chosen.length === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}
